import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../core/modules/global';
import { UPDATE_RECEPTION_ORDER_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useCompanyCore } from '../../../../core/modules/company';
import { useReceptionCore } from '../../../../core/modules/reception';
import { useBusinessCore } from '../../../../core/modules/business';
import moment from 'moment';
import { DOCUMENT_TYPES } from '../../../../core/constants/documentTypes';
import { useMasterCore } from '../../../../core/modules/master';
import { useMeCore } from '../../../../core/modules/me';
import Swal from 'sweetalert2';

const UpdateDialog = () => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { receptionOrder, putReceptionOrder, getReceptionOrders, deleteReceptionOrder } = useReceptionCore();
  const { companyId } = useCompanyCore();
  const { user } = useMeCore();
  const { businesses } = useBusinessCore();
  const { acquisitionTypes } = useMasterCore();

  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const canWrite = useMemo(() => user && (user.privileges.superAdmin || user.privileges.writeReception), [user]); 

  // Esquema de validación base
  const baseSchema = yup.object().shape({
    reception_date: yup.string().required('Campo requerido'),
    description: yup.string().required('Campo requerido'),
    cost: yup.number().min(0, 'Mínimo valor permitido es 0').typeError('Formato incorrecto'),
    document_number: yup.string().optional(),
    document_type_id: yup.string().optional(),
    business_id: yup.number().required('Campo requerido'),
    area_id: yup.number().required('Campo requerido'),
    acquisition_type_id: yup.string().required('Campo requerido'),
    commission: yup.number().min(0, 'Mínimo valor permitido es 0').max(100, 'Máximo valor permitido es 100').typeError('Formato incorrecto'),
    notes: yup.string().optional(),
  });

  const { control, formState, handleSubmit, reset, setValue, watch } = useForm({
    mode: 'onChange',
    resolver: yupResolver(baseSchema),
  });

  const { isValid, errors } = formState;

  // Observar cambios en el proveedor, área y tipo de adquisición seleccionados
  const selectedBusinessId = watch('business_id');
  const selectedAreaId = watch('area_id');
  const selectedAcquisitionTypeId = watch('acquisition_type_id');

  // Obtener información del tipo de adquisición seleccionado
  const selectedAcquisitionType = useMemo(() => {
    if (!selectedAcquisitionTypeId) return null;
    return (
      acquisitionTypes.find((type) => type.id === Number(selectedAcquisitionTypeId)) || null
    );
  }, [selectedAcquisitionTypeId, acquisitionTypes]);

  const resetForm = useCallback(() => {
    reset({});
    closeDialog(UPDATE_RECEPTION_ORDER_DIALOG);
  }, [closeDialog, reset]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId) {
        setLoading(true);
        putReceptionOrder(
          companyId,
          receptionOrder?.id!,
          {
            ...values,
            document_type_id: values.document_type_id ? +values.document_type_id : undefined,
            acquisition_type_id: +values.acquisition_type_id,
            business_area_id: +values.area_id,
            reception_date: moment(values.reception_date).unix()
          },
          () => {
            resetForm();
            getReceptionOrders(companyId);
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    },
    [companyId, putReceptionOrder, receptionOrder?.id, resetForm, getReceptionOrders]
  );

  const onDelete = useCallback(() => {
    console.log('onDelete: receptionOrder:', receptionOrder);
    console.log('onDelete: has_receptions:', receptionOrder?.has_receptions);
    
    Swal.fire({
      title: "Confimar acción",
      text: `Eliminar la orden de ingreso ${receptionOrder?.document_number}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar!",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed && companyId && receptionOrder) {
        setLoading(true);

        deleteReceptionOrder(
          companyId,
          receptionOrder.id,
          () => {
            setLoading(false);
            getReceptionOrders(companyId);
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [receptionOrder, companyId, deleteReceptionOrder, getReceptionOrders, resetForm]);

  // Función helper para encontrar el área principal de una empresa
  const findBusinessMainArea = useCallback((business: any) => {
    if (!business?.areas || business.areas.length === 0) return null;
    // El área principal es la que tiene el mismo acrónimo que la empresa
    return (
      business.areas.find((area: any) => area.acronym === business.acronym) ||
      business.areas[0]
    );
  }, []);

  const providers = useMemo(() => {
    return businesses.filter((business) => business.is_provider);
  }, [businesses]);

  // Obtener áreas del proveedor seleccionado
  const availableAreas = useMemo(() => {
    if (!selectedBusinessId) return [];
    const selectedBusiness = businesses.find(
      (b) => b.id === Number(selectedBusinessId)
    );
    return selectedBusiness?.areas || [];
  }, [selectedBusinessId, businesses]);

  // Obtener información del área seleccionada
  const selectedProviderArea = useMemo(() => {
    if (!selectedAreaId || availableAreas.length === 0) return null;
    return (
      availableAreas.find((area) => area.id === Number(selectedAreaId)) || null
    );
  }, [selectedAreaId, availableAreas]);

  useEffect(() => {
    if (receptionOrder) {
      setIsInitialLoad(true);
      // El backend envía Unix timestamp en segundos, JavaScript necesita milisegundos
      const receptionDate = moment.unix(receptionOrder.reception_date).format('YYYY-MM-DD');
      setValue("reception_date", receptionDate);
      setValue("description", receptionOrder.description);
      setValue("cost", receptionOrder.cost);
      setValue("document_number", receptionOrder.document_number);
      setValue("document_type_id", receptionOrder.document_type_id);
      setValue("business_id", receptionOrder.business_id);
      setValue("area_id", receptionOrder.business_area_id);
      setValue("acquisition_type_id", receptionOrder.acquisition_type_id);
      setValue("commission", receptionOrder.commission);
      setValue("notes", receptionOrder.notes);
      // Marcar como carga inicial completada después de un pequeño delay
      setTimeout(() => {
        setIsInitialLoad(false);
      }, 100);
    }
  }, [receptionOrder, setValue]);

  // Efecto para limpiar área y actualizar con área principal cuando cambie el proveedor
  // Solo ejecutar si NO es la carga inicial (para evitar sobrescribir el valor guardado)
  useEffect(() => {
    if (!isInitialLoad && selectedBusinessId) {
      const selectedBusiness = businesses.find(
        (b) => b.id === Number(selectedBusinessId)
      );
      if (selectedBusiness) {
        const mainArea = findBusinessMainArea(selectedBusiness);
        if (mainArea) {
          // Auto-seleccionar el área principal
          setValue('area_id', mainArea.id);
          setValue('commission', mainArea.commission);
        }
      }
    }
  }, [isInitialLoad, selectedBusinessId, businesses, findBusinessMainArea, setValue]);

  // Efecto para actualizar la comisión cuando cambie el área
  // Solo actualizar si NO es la carga inicial (para evitar sobrescribir el valor guardado)
  useEffect(() => {
    if (!isInitialLoad && selectedAreaId && availableAreas.length > 0) {
      const selectedArea = availableAreas.find(
        (area) => area.id === Number(selectedAreaId)
      );
      if (selectedArea?.commission) {
        setValue('commission', selectedArea.commission);
      }
    }
  }, [selectedAreaId, availableAreas, setValue, isInitialLoad]);

  // Efecto para actualizar costo y comisión cuando cambie el tipo de adquisición
  // Solo ejecutar si NO es la carga inicial (para evitar sobrescribir el valor guardado)
  useEffect(() => {
    if (!isInitialLoad && selectedAcquisitionType) {
      if (!selectedAcquisitionType.requires_cost) {
        setValue('cost', 0);
      }
      if (!selectedAcquisitionType.requires_commission) {
        setValue('commission', 0);
      }
    }
  }, [isInitialLoad, selectedAcquisitionType, setValue]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && Array.isArray(dialogs) && dialogs.includes(UPDATE_RECEPTION_ORDER_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Modificar orden de ingreso
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form
                  noValidate
                  onSubmit={handleSubmit(onSubmit)}
                  className="flex flex-col space-y-24"
                >
                  <Controller
                    name="reception_date"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Fecha de Recepción"
                        type="date"
                        variant="outlined"
                        required
                        fullWidth
                      />
                    )}
                  />

                  <Controller
                    name="business_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel>Proveedor</InputLabel>
                        <Select {...field} label="Proveedor" disabled>
                          {providers.map((seller) => (
                            <MenuItem key={seller.id} value={seller.id}>
                              {seller.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  {selectedBusinessId && availableAreas.length > 0 && (
                    <Controller
                      name="area_id"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth>
                          <InputLabel>Área</InputLabel>
                          <Select
                            {...field}
                            label="Área"
                            disabled
                            error={!!errors.area_id}
                          >
                            {availableAreas.map((area) => (
                              <MenuItem key={area.id} value={area.id}>
                                {area.acronym} - {area.commission}%
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.area_id && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 1, ml: 2 }}
                            >
                              {errors.area_id.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  )}

                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Descripción"
                        variant="outlined"
                        required
                        fullWidth
                        inputProps={{
                          style: { textTransform: 'uppercase' }
                        }}
                        onChange={(e) => {
                          field.onChange(e.target.value.toUpperCase());
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="acquisition_type_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl variant="outlined" fullWidth required>
                        <InputLabel id="type-label">
                          Tipo de adquisición
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="type-label"
                          label="Tipo de adquisición"
                          disabled
                          required
                        >
                          {acquisitionTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  {selectedAcquisitionType?.requires_cost && (
                    <Controller
                      name="cost"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Costo"
                          type="number"
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  )}

                  <Controller
                    name="document_type_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel id="document_type-label">
                          Tipo de documento
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="document_type-label"
                          id="document_type-label"
                          disabled={loading}
                          label="Tipo de documento"
                        >
                          {DOCUMENT_TYPES.map(({ name, value }) => (
                            <MenuItem key={value} value={value}>
                              {name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="document_number"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Número de Documento"
                        variant="outlined"
                        fullWidth
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      />
                    )}
                  />

                  {selectedAcquisitionType?.requires_commission && (
                    <Controller
                      name="commission"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={
                            selectedProviderArea
                              ? `% de Comisión - Área: ${selectedProviderArea.acronym}`
                              : '% de Comisión'
                          }
                          type="number"
                          variant="outlined"
                          error={!!errors.commission}
                          helperText={
                            errors.commission?.message ||
                            (selectedProviderArea
                              ? `Comisión del área "${selectedProviderArea.acronym}" (${selectedProviderArea.commission}%). Puede modificar este valor.`
                              : selectedBusinessId
                              ? 'Seleccione un área para obtener la comisión automáticamente.'
                              : 'Seleccione un proveedor y área para obtener la comisión automáticamente.')
                          }
                          fullWidth
                          inputProps={{
                            min: 0,
                            max: 100,
                            step: 0.1,
                          }}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Validar que no sea mayor a 100
                            if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                              field.onChange(value);
                            }
                          }}
                        />
                      )}
                    />
                  )}



                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        multiline
                        label="Observación"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />

                  {canWrite && (
                    <div className="flex justify-center gap-4">
                      {!receptionOrder?.has_receptions && (
                        <Button
                          variant="contained"
                          color="error"
                          sx={{ borderRadius: 50 }}
                          disabled={loading}
                          type="button"
                          onClick={onDelete}
                        >
                          <Spinner show={loading} />
                          Eliminar
                        </Button>
                      )}
                      {receptionOrder?.has_receptions && (
                        <div className="text-sm text-gray-500 italic">
                          No se puede eliminar: tiene recepciones asociadas
                        </div>
                      )}
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{ borderRadius: 50 }}
                        disabled={!isEmpty(errors) || !isValid || loading}
                        type="submit"
                        size="large"
                      >
                        <Spinner show={loading} />
                        Guardar
                      </Button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDialog;
